import React, { useState, useRef, useEffect, useMemo } from 'react';

// A searchable single-select combobox. Type to filter options by their `search`
// text (fall back to label). Used for product pickers so staff can find an item
// by name OR product id.
//   options: [{ value, label, sub?, search? }]
//   value:   the selected option's value ('' = none)
//   onChange(value)
const SearchSelect = ({ options, value, onChange, placeholder = 'Search…', disabled, style, maxItems = 60 }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(() => options.find((o) => String(o.value) === String(value)) || null, [options, value]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const base = s ? options.filter((o) => String(o.search != null ? o.search : o.label).toLowerCase().includes(s)) : options;
    return base.slice(0, maxItems);
  }, [options, q, maxItems]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) { setOpen(false); setQ(''); } };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  const pick = (o) => { onChange(o.value); setOpen(false); setQ(''); };
  const openMenu = () => { if (!disabled) { setOpen(true); setHi(0); } };

  return (
    <div className={`search-select${disabled ? ' disabled' : ''}`} ref={boxRef} style={style}>
      <div className="ss-control" onClick={openMenu}>
        {open ? (
          <input ref={inputRef} className="ss-input" value={q} placeholder={selected ? selected.label : placeholder}
            onChange={(e) => { setQ(e.target.value); setHi(0); }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, filtered.length - 1)); }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
              else if (e.key === 'Enter') { e.preventDefault(); if (filtered[hi]) pick(filtered[hi]); }
              else if (e.key === 'Escape') { setOpen(false); setQ(''); }
            }} />
        ) : (
          <span className={`ss-value${selected ? '' : ' ss-placeholder'}`}>{selected ? selected.label : placeholder}</span>
        )}
        <span className="ss-caret">▾</span>
      </div>
      {open && (
        <div className="ss-menu">
          {filtered.map((o, i) => (
            <div key={o.value} className={`ss-opt${i === hi ? ' hi' : ''}${String(o.value) === String(value) ? ' sel' : ''}`}
              onMouseEnter={() => setHi(i)} onMouseDown={(e) => { e.preventDefault(); pick(o); }}>
              <span className="ss-opt-label">{o.label}</span>{o.sub != null && <span className="ss-sub">{o.sub}</span>}
            </div>
          ))}
          {!filtered.length && <div className="ss-empty">No matches</div>}
        </div>
      )}
    </div>
  );
};

export default SearchSelect;
